import React, { useEffect, useState } from "react";
import { Button, Card, Input, Space, Spin, Switch, Typography, message } from "antd";
import { DragOutlined, SaveOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { produceApi, ProduceOrderItem } from "../../api/produceApi";
import { useAuth } from "../../contexts/auth.context";
import { hasAdminAccess } from "../../utils/roleUtils";

const { Title, Text } = Typography;

interface RowContentProps {
  item: ProduceOrderItem;
  index: number;
}

const RowContent: React.FC<RowContentProps & { draggable?: boolean }> = ({ item, index, draggable }) => (
  <>
    <DragOutlined style={{ color: draggable ? "#999" : "#d9d9d9" }} />
    <Text type="secondary" style={{ width: 32 }}>
      {index + 1}
    </Text>
    <Text strong style={{ flex: 1 }}>
      {item.name}
    </Text>
    <Text type="secondary">{item.unitOfMeasurement}</Text>
    {item.isHidden && <Text type="warning">скрыт</Text>}
  </>
);

const rowBaseStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 14px",
  marginBottom: 8,
  background: "#fff",
  border: "1px solid #f0f0f0",
  borderRadius: 8,
};

const SortableRow: React.FC<RowContentProps> = ({ item, index }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item._id });

  const style: React.CSSProperties = {
    ...rowBaseStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    border: isDragging ? "2px dashed #1890ff" : rowBaseStyle.border,
    cursor: "grab",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <RowContent item={item} index={index} draggable />
    </div>
  );
};

const StaticRow: React.FC<RowContentProps> = ({ item, index }) => (
  <div style={{ ...rowBaseStyle, cursor: "default" }}>
    <RowContent item={item} index={index} />
  </div>
);

const ProduceOrderPage: React.FC = () => {
  const { authState } = useAuth();
  const isAdmin = hasAdminAccess(authState.user?.role);

  const [items, setItems] = useState<ProduceOrderItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [showHidden, setShowHidden] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await produceApi.getOrderList(showHidden);
      setItems(data);
      setDirty(false);
    } catch (error) {
      message.error("Не удалось загрузить список продукции");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHidden]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((i) => i._id === active.id);
      const newIndex = prev.findIndex((i) => i._id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = items.map((item, index) => ({ id: item._id, sortOrder: index }));
      await produceApi.updateSortOrder(payload);
      message.success("Порядок продукции сохранён");
      setDirty(false);
    } catch (error) {
      message.error("Не удалось сохранить порядок");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Недостаточно прав для изменения порядка продукции.</Text>
      </div>
    );
  }

  // Поиск не мешает перетаскиванию: если строка поиска задана, DnD отключаем,
  // чтобы не менять реальный порядок при отфильтрованном виде.
  const normalizedSearch = search.trim().toLowerCase();
  const filtered = normalizedSearch
    ? items.filter((i) => i.name.toLowerCase().includes(normalizedSearch))
    : items;
  const dndEnabled = !normalizedSearch;

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: "0 auto" }}>
      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}
        wrap
      >
        <Title level={3} style={{ margin: 0 }}>
          Порядок продукции
        </Title>
        <Space wrap>
          <Space size={4}>
            <Text>Показывать скрытые</Text>
            <Switch checked={showHidden} onChange={setShowHidden} />
          </Space>
          <Button icon={<ReloadOutlined />} onClick={fetchItems} disabled={saving}>
            Сбросить
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!dirty}
          >
            Сохранить порядок
          </Button>
        </Space>
      </Space>

      <Text type="secondary">
        Перетаскивайте строки, чтобы задать порядок. Он применяется в разделах
        продукции, прайса, в КП и в выгрузках прайса.
      </Text>

      <Input.Search
        placeholder="Поиск по названию"
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ margin: "16px 0" }}
      />

      {!dndEnabled && (
        <Text type="warning" style={{ display: "block", marginBottom: 8 }}>
          При активном поиске перетаскивание отключено. Очистите поиск, чтобы менять порядок.
        </Text>
      )}

      <Card bodyStyle={{ padding: 12 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Text type="secondary">Нет продукции</Text>
          </div>
        ) : dndEnabled ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filtered.map((i) => i._id)}
              strategy={verticalListSortingStrategy}
            >
              {filtered.map((item, index) => (
                <SortableRow key={item._id} item={item} index={index} />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          filtered.map((item, index) => (
            <StaticRow key={item._id} item={item} index={index} />
          ))
        )}
      </Card>
    </div>
  );
};

export default ProduceOrderPage;
